import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import { t } from 'i18next';
import { memo, useCallback, useContext, useMemo } from 'react';

import Checkbox from '~components/Checkbox/Checkbox';
import PrevailingWageBadge from '~components/Common/PrevailingWageBadge';
import { BottomColumn } from '~components/Order/ordersDispatchColumns';
import { OverflowAwareText } from '~components/Order/ordersDispatchStyledComponents';
import OrderStatusChip from '~components/Order/OrderStatusChip';
import OrderCard from '~components/OrderCard/OrderCard';
import OrderCardSubtitle from '~components/OrderCard/OrderCardSubtitle';
import OrderCardTitle from '~components/OrderCard/OrderCardTitle';
import { JobCardPdfRecipientType } from '~hooks/useJobCardPdf/models';
import { Order } from '~hooks/useOrders/models';
import {
  EditOrderCTA,
  ThreeDotsMenu,
} from '~pages/Dispatch/components/OrderDispatchCardFragments';
import { Summary } from '~pages/Dispatch/components/OrderSummary';

import FilesPageContext from '../../FilesPageContext';
import ExpandedBlock from './ExpandedBlock/ExpandedBlock';

interface OrdersListCardProps {
  isEditInProgress?: boolean;
  onCancelOrder: (order: Order) => void;
  onCloneOrder: (order: Order) => void;
  onCopyAndEditOrder: (order: Order) => void;
  onDownloadFiles: (jobIds: string[]) => void;
  onDownloadJobCardPdfs: (
    jobIds: string[],
    recipientType: JobCardPdfRecipientType,
  ) => void;
  onEditOrder: (order: Order) => void;
  onTextAllDrivers: (order: Order) => void;
  order: Order;
}

function OrdersListCard({
  isEditInProgress,
  onCancelOrder,
  onCloneOrder,
  onCopyAndEditOrder,
  onDownloadFiles,
  onDownloadJobCardPdfs,
  onEditOrder,
  onTextAllDrivers,
  order,
}: OrdersListCardProps) {
  const { getJobsInOrder, getOrderCheckedState, toggleOrderCheck } =
    useContext(FilesPageContext);

  const { orderCardsExpandedState, toggleSingleOrderCardsExpandedState } =
    useContext(FilesPageContext);

  const { orderId } = order;
  const checkedState = getOrderCheckedState(order);

  const isExpanded = useMemo(() => {
    const expandedState =
      orderCardsExpandedState.byOrderId[orderId] ?? orderCardsExpandedState.allCards;

    return expandedState === 'expanded';
  }, [orderCardsExpandedState]);

  const toggleExpanded = useCallback(() => {
    toggleSingleOrderCardsExpandedState(orderId, isExpanded ? 'collapsed' : 'expanded');
  }, [orderId, isExpanded]);

  const editOrder = useCallback(() => {
    onEditOrder(order);
  }, [onEditOrder, order]);

  const cancelOrder = useCallback(() => {
    onCancelOrder(order);
  }, [onCancelOrder, order]);

  const cloneOrder = useCallback(() => {
    onCloneOrder(order);
  }, [onCloneOrder, order]);

  const copyAndEditOrder = useCallback(() => {
    onCopyAndEditOrder(order);
  }, [onCopyAndEditOrder, order]);

  const textAllDrivers = useCallback(() => {
    onTextAllDrivers(order);
  }, [onTextAllDrivers, order]);

  const hasListedJobs = getJobsInOrder(order).length > 0;

  const getListedJobIds = useCallback(() => {
    return getJobsInOrder(order).map((job) => job.id);
  }, [getJobsInOrder, order]);

  const downloadFiles = useCallback(() => {
    onDownloadFiles(getListedJobIds());
  }, [getListedJobIds, onDownloadFiles]);

  const downloadJobCardPdfs = useCallback(
    (recipientType: JobCardPdfRecipientType) => {
      onDownloadJobCardPdfs(getListedJobIds(), recipientType);
    },
    [getListedJobIds, onDownloadJobCardPdfs],
  );

  return (
    <OrderCard>
      <Box
        onClick={toggleExpanded}
        sx={{
          alignItems: 'flex-start',
          display: 'grid',
          gap: 1,
          gridTemplateColumns: '20px 1fr auto',
          p: 1,
        }}
      >
        <Checkbox
          isChecked={checkedState === true}
          isIntermediate={checkedState === 'intermediate'}
          onChange={() => toggleOrderCheck(order)}
        />

        <Box
          sx={{ display: 'flex', flexDirection: 'column', gap: 1, overflow: 'hidden' }}
        >
          <OrderCardTitle order={order} />
          <OrderCardSubtitle order={order} />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {order.prevailingWage && <PrevailingWageBadge />}
          <OrderStatusChip order={order} hideDropdownArrow />

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <EditOrderCTA
              isEditInProgress={isEditInProgress}
              order={order}
              onClick={editOrder}
            />

            <ThreeDotsMenu
              onCancelClick={cancelOrder}
              onCloneOrderClick={cloneOrder}
              onCopyAndEditClick={copyAndEditOrder}
              onDownloadFilesClick={hasListedJobs ? downloadFiles : undefined}
              onDownloadJobCardPdfsClick={hasListedJobs ? downloadJobCardPdfs : undefined}
              onTextAllDriversClick={textAllDrivers}
              order={order}
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            gap: 0.5,
            gridColumn: '2 / 4',
            overflow: 'hidden',
          }}
        >
          <BottomColumn
            omitWrapper
            order={order}
            statsSlot={<StatsSlot order={order} />}
          />
        </Box>
      </Box>

      <Collapse in={isExpanded} mountOnEnter unmountOnExit>
        <Box
          sx={(theme) => ({ borderTop: `solid 1px ${theme.brandV2.colors.treadGray7}` })}
        >
          <ExpandedBlock order={order} />
        </Box>
      </Collapse>
    </OrderCard>
  );
}

interface StatsSlotProps {
  order: Order;
}

function StatsSlot({ order }: StatsSlotProps) {
  const { orderSummary } = order;

  const loadsCount = orderSummary?.loadsCount ?? 0;
  const approvedLoadsCount = orderSummary?.approvedLoadsCount ?? 0;
  const deliveredLoadsCount = orderSummary?.deliveredLoadsCount ?? 0;
  const approvedPayables = orderSummary?.approvedPayables ?? 0;
  const totalPayables = orderSummary?.totalPayables ?? 0;
  const approvedReceivables = orderSummary?.approvedReceivables ?? 0;
  const totalReceivables = orderSummary?.totalReceivables ?? 0;

  return (
    <>
      <Widget
        bottomRowDescription={t(
          'approvals.files.order_card_stats.payables_approved.description',
        )}
        summaryCount={approvedPayables}
        summaryTotal={totalPayables}
        summaryDescription={t(
          'approvals.files.order_card_stats.payables_approved.summary_text',
          { approved: approvedPayables, total: totalPayables },
        )}
      />

      <Widget
        bottomRowDescription={t(
          'approvals.files.order_card_stats.receivables_approved.description',
        )}
        summaryCount={approvedReceivables}
        summaryTotal={totalReceivables}
        summaryDescription={t(
          'approvals.files.order_card_stats.receivables_approved.summary_text',
          { approved: approvedReceivables, total: totalReceivables },
        )}
      />

      <Widget
        bottomRowDescription={t(
          'approvals.files.order_card_stats.loads_approved.description',
        )}
        summaryCount={approvedLoadsCount}
        summaryTotal={loadsCount}
        summaryDescription={t(
          'approvals.files.order_card_stats.loads_approved.summary_text',
          { approved: approvedLoadsCount, total: loadsCount },
        )}
      />

      <Widget
        bottomRowDescription={t(
          'approvals.files.order_card_stats.loads_delivered.description',
        )}
        summaryCount={deliveredLoadsCount}
        summaryTotal={loadsCount}
        summaryDescription={t(
          'approvals.files.order_card_stats.loads_delivered.summary_text',
          { delivered: deliveredLoadsCount, total: loadsCount },
        )}
      />
    </>
  );
}

interface WidgetProps {
  bottomRowDescription: string;
  summaryCount: number;
  summaryDescription: string;
  summaryTotal: number;
}

function Widget({
  bottomRowDescription,
  summaryCount,
  summaryDescription,
  summaryTotal,
}: WidgetProps) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Summary
        count={summaryCount}
        description={summaryDescription}
        total={summaryTotal}
        descriptionSx={{ mb: 0.5, fontWeight: 500, fontSize: '14px' }}
      />

      <OverflowAwareText
        sx={(theme) => ({ fontSize: '12px', color: theme.brandV2.colors.treadGray3 })}
      >
        {bottomRowDescription}
      </OverflowAwareText>
    </Box>
  );
}

export default memo(OrdersListCard);
